/**
 * AIアシスタントサービス
 *
 * バックエンドのAIアシスタントAPIとの通信を管理
 */

import { getAuthInstance } from "../lib/firebase";
import { ASSISTANT_API_BASE_URL } from "../constants/api";

// ======================================
// 型定義
// ======================================

/**
 * ページコンテキスト（現在閲覧中のページ情報）
 */
export interface PageContext {
  path: string;
  data: Record<string, unknown>;
}

/**
 * 添付画像
 */
export interface AttachedImage {
  mimeType: string;
  data: string; // Base64エンコード
}

/**
 * チャットリクエスト
 */
export interface ChatRequest {
  sessionId?: string;
  message: string;
  pageContext: PageContext;
  images?: AttachedImage[];
}

/**
 * チャットレスポンス
 */
export interface ChatResponse {
  response: string;
  sessionId: string;
  createdNewSession: boolean;
}

/**
 * セッション一覧のアイテム
 */
export interface SessionSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

/**
 * メッセージ
 */
export interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  hasImages?: boolean;
  /** フロントエンド表示用の画像URL（data URL形式、Firestoreには保存されない） */
  imageUrls?: string[];
}

/**
 * セッション詳細
 */
export interface SessionDetail {
  id: string;
  title: string;
  messages: Message[];
  pageContext: PageContext;
  createdAt: string;
  updatedAt: string;
}

// ======================================
// ユーティリティ
// ======================================

/**
 * 認証ヘッダーを取得
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const auth = getAuthInstance();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("認証されていません");
  }

  const token = await user.getIdToken();

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

/**
 * APIレスポンスをハンドリング
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "不明なエラー" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

// ======================================
// API関数
// ======================================

/**
 * AIにメッセージを送信
 */
export async function sendMessage(request: ChatRequest, signal?: AbortSignal): Promise<ChatResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ASSISTANT_API_BASE_URL}/chat`, {
    method: "POST",
    headers,
    body: JSON.stringify(request),
    signal,
  });
  return handleResponse<ChatResponse>(response);
}

/**
 * セッション一覧を取得
 */
export async function fetchSessions(signal?: AbortSignal): Promise<SessionSummary[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ASSISTANT_API_BASE_URL}/sessions`, { headers, signal });
  const data = await handleResponse<{ sessions: SessionSummary[] }>(response);
  return data.sessions;
}

/**
 * セッション詳細を取得
 */
export async function fetchSessionDetail(sessionId: string, signal?: AbortSignal): Promise<SessionDetail> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ASSISTANT_API_BASE_URL}/sessions/${sessionId}`, { headers, signal });
  const data = await handleResponse<{ session: SessionDetail }>(response);
  return data.session;
}
